import RichText from '../rich-text'
import colors from '@/constants/colors'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'

import type { Entry } from 'contentful'
import type { TypeComponentCardSkeleton, TypeComponentTableSkeleton, TypeSectionSetOfCardsSkeleton } from '@/types/ctf'

import ImageContentful from '../image-contentful'
import Table from '@/components/table'
import Image from '@/components/ui/image'

type SetOfCardsEntry = Entry<TypeSectionSetOfCardsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type CardEntry = Entry<TypeComponentCardSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type TableEntry = Entry<TypeComponentTableSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type PresentationSlidesProps = Partial<SetOfCardsEntry['fields']> & {
  table?: TableEntry
}

const isPrintView = isPrint()

export const PresentationSlideWrapper = ({
  children,
  cardsLength = 1,
}: {
  children: React.ReactNode
  cardsLength?: number
}) => {
  return (
    <div className={cx(isPrintView && 'h-[85vh] w-[100vw] pt-[70px] px-10')}>
      <div
        className={cx(
          'w-full relative bg-white dark:bg-secondary-800',
          isPrintView && 'rounded-4xl border overflow-hidden '
        )}
      >
        <div style={{ paddingTop: '56.25%' }}>
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="h-full w-full">
              <div
                className={cx(
                  'grid justify-center items-center h-full w-full',
                  cardsLength === 1 && 'grid-cols-1',
                  cardsLength === 2 && 'grid-cols-2',
                  cardsLength === 3 && 'grid-cols-3',
                  cardsLength === 4 && 'grid-cols-4'
                )}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function PresentationSlide({
  table,
  cards,
  cardIconSize = '1/2',
  cardIconLocation,
}: PresentationSlidesProps): JSX.Element {
  const BodyWrapper = ({ children, className }: { children: React.ReactNode; className?: string }) => {
    return <div className={cx('p-10 flex items-center justify-center h-full', className)}>{children}</div>
  }

  const checkAsset = (asset) => {
    if (typeof asset.fields === 'object' && asset.sys.contentType.sys.id === 'componentTable') {
      const { fields } = asset as TableEntry
      return <Table {...fields} />
    }
  }

  return (
    <PresentationSlideWrapper cardsLength={cards?.length}>
      {table && typeof table === 'object' && <Table {...table} />}
      {cards &&
        cards.length === 1 &&
        cards
          ?.filter((card): card is CardEntry => card !== undefined)
          .map((card) => {
            if (!card?.fields) return null
            const {
              fields: { headline, subtext, image, asset },
            } = card
            return (
              <div
                key={`card-${card.sys.id}`}
                className={cx(
                  'flex justify-center items-center h-full w-full',
                  cardIconLocation === 'Right' && 'flex-row',
                  cardIconLocation === 'Left' && 'flex-row-reverse'
                )}
              >
                <BodyWrapper
                  className={cx(
                    (!cardIconSize || cardIconSize === '1/2') && 'w-1/2',
                    cardIconSize === '1/3' && 'w-2/3',
                    cardIconSize === '1/4' && 'w-3/4',
                    !image && 'w-full'
                  )}
                >
                  <div className={cx(!image && 'text-center')}>
                    {headline && <h2 className="header-gradient-reset">{headline}</h2>}

                    {subtext && (
                      <div
                        className={cx(
                          !image &&
                            '[&_figure]:mx-auto [&_figure]:w-2/3 [&_svg]:mx-auto [&_svg]:w-2/3! [&_img]:w-2/3! [&_img]:mx-auto'
                        )}
                      >
                        <RichText body={subtext} renderImage={(ref) => <Image image={ref} showCaption={false} />} />
                      </div>
                    )}
                    {asset && checkAsset(asset)}
                  </div>
                </BodyWrapper>
                {image && (
                  <BodyWrapper
                    className={cx(
                      (!cardIconSize || cardIconSize === '1/2') && 'w-1/2',
                      cardIconSize === '1/3' && 'w-1/3',
                      cardIconSize === '1/4' && 'w-1/4'
                    )}
                  >
                    <ImageContentful {...image.fields} />
                  </BodyWrapper>
                )}
              </div>
            )
          })}

      {cards &&
        cards.length > 1 &&
        cards
          ?.filter((card): card is CardEntry => card !== undefined)
          .map((card) => {
            if (!card?.fields) return null
            const { asset, headline, subtext, image } = card.fields

            return (
              <BodyWrapper key={`card-${card.sys.id}`}>
                <div>
                  {headline && <h2 className="header-gradient-reset">{headline}</h2>}
                  {subtext && (
                    <div className="[&_figure]:w-2/3 [&_svg]:w-2/3! [&_img]:w-2/3!">
                      <RichText body={subtext} renderImage={(ref) => <Image image={ref} showCaption={false} />} />
                    </div>
                  )}
                  {image && <ImageContentful {...image.fields} />}
                  {asset && checkAsset(asset)}
                </div>
              </BodyWrapper>
            )
          })}
    </PresentationSlideWrapper>
  )
}
